import { fetchJSON, postJSON, delJSON } from '@haina/utils-request'

/**
 * 模板管理中心 - 接口 action
 *
 * 后端接口约定：
 *   GET    /ht_templates/list              获取模板列表（模板文件扫描，含 group / type / update_time）
 *   GET    /ht_templates/page           获取模板页面（测试预览用，参数 coli_sn / dei_sn / type / lgc）
 *   GET    /ht_templates/page/list      获取页面版本列表（按 coli_sn + type，id 倒序）
 *                                       with_content=0 仅返回元数据（默认 1 含 content，兼容旧调用）
 *   GET    /ht_templates/page/:id       按 id 取单个版本（含 content），配合 with_content=0 按需加载
 *   POST   /ht_templates/page           保存页面（有 id 更新，无 id 新增），返回保存后的行元数据
 *   DELETE /ht_templates/page/:id       删除页面（按 id）
 *   POST   /ht_templates/generate       生成信函（新建，参数 coli_sn / dei_sn / type / lgc），返回 id + html + 行元数据
 *   POST   /ht_templates/publish        发布页面 HTML 到 OSS
 *
 * 模板以项目文件 {事业部code}_{类型}.html 管理（git 版本控制），事业部 code 与 dei_sn 的映射
 * 配置在后端 config.py 的 DEI_GROUP。不提供模板 CRUD 接口。
 * 错误处理：@haina/utils-request 在 errcode !== 0 或 HTTP 非 2xx 时已抛出含完整信息的 Error
 * （业务错误为 `errmsg: errcode`，HTTP 错误为 `Fetch error: <url> <status>`），
 * 故此处各 action 不再做兜底，错误向上抛给调用方，由界面把完整信息展示给用户。
 */

const BASE = import.meta.env.DEV
  ? 'http://localhost:8013/ht_templates'
  : 'https://p9axztuwd7x8a7.mycht.cn/ht3-templates/ht_templates'

/**
 * 获取模板列表
 * @param {object} params { dei_sn?, type? }
 * @returns {Promise<Array>}
 */
export const getTemplateListAction = async (params = {}) => {
  const { result } = await fetchJSON(`${BASE}/list`, params)
  return result || []
}

/**
 * 获取模板页面（测试预览用，按订单渲染信函内容）
 * @param {object} params { coli_sn, dei_sn, type, lgc }
 * @returns {Promise<object>}
 */
export const getTemplatePageAction = async (params = {}) => {
  const { result } = await fetchJSON(`${BASE}/page`, params)
  return result || {}
}

/**
 * 根据订单号查询订单序号
 * @param {string} coli_id
 * @returns {Promise<object>}
 */
export const resolveColiSnAction = async (coli_id) => {
  const { result } = await fetchJSON(`${BASE}/resolve-order`, { coli_id })
  return result || {}
}

/**
 * 获取页面版本列表（按 coli_sn + type，id 倒序）
 * 默认 with_content=0 只取元数据（列表只需标题/时间，避免搬运各版本 content）；
 * 版本内容改用 getPageByIdAction 按需拉取。
 * @param {object} params { coli_sn, type, with_content? }
 * @returns {Promise<Array>}
 */
export const getPageListAction = async (params = {}) => {
  const { result } = await fetchJSON(`${BASE}/page/list`, { with_content: 0, ...params })
  return result || []
}

/**
 * 按 id 获取单个页面版本（含 content）
 * @param {number|string} pageId
 * @returns {Promise<object>}
 */
export const getPageByIdAction = async (pageId) => {
  const { result } = await fetchJSON(`${BASE}/page/${pageId}`, {})
  return result || {}
}

/**
 * 保存页面（有 id 更新，无 id 新增）
 * @param {object} body { id, coli_sn, dei_sn, type, title, content, link }
 */
export const savePageAction = async (body) => {
  const { result } = await postJSON(`${BASE}/page`, body)
  return result || {}
}

/**
 * 删除页面（按 id）
 * @param {number|string} id
 */
export const deletePageAction = async (id) => {
  const { result } = await delJSON(`${BASE}/page/${id}`, {})
  return result || {}
}

/**
 * 生成信函（新建）
 * @param {object} params { coli_sn, dei_sn, type, lgc }
 * @returns {Promise<object>}
 */
export const generateTemplateAction = async (params = {}) => {
  const { result } = await postJSON(`${BASE}/generate`, params)
  return result || {}
}

/**
 * 发布页面 HTML 到 OSS
 * @param {object} body { id, html }
 */
export const publishPageAction = async (body) => {
  const { result } = await postJSON(`${BASE}/publish`, body)
  return result || {}
}

/**
 * 将页面 HTML 导出为 PDF 存本地，返回 PDF 相对地址与邮件附件名（确认信）
 * 返回的 pdf_url 为后端相对路径（形如 /pdf/2026/xxx.pdf），
 * 直连后端时用 toAbsolutePdfUrl 拼成绝对 URL 再打开或挂附件
 * @param {object} body { id, html }
 * @returns {Promise<object>} { pdf_url, pdf_name }
 */
export const exportPdfAction = async (body) => {
  const { result } = await postJSON(`${BASE}/export-pdf`, body)
  const { pdf_url, pdf_name } = result || {}
  return { pdf_url, pdf_name }
}

/**
 * 把后端返回的 PDF 相对地址拼成绝对 URL（供 window.open 与邮件附件预取使用）
 * @param {string} url 后端返回的 pdf_url（或已是绝对地址）
 * @returns {string}
 */
export const toAbsolutePdfUrl = (url) => {
  if (!url) return ''
  return url.startsWith('http') ? url : `${BASE}${url}`
}

/**
 * 重新生成页面 AI 标题和正文
 * @param {number|string} pageId
 * @param {object} body { lgc }
 */
export const generateAiLetterAction = async (pageId, body = {}) => {
  const { result } = await postJSON(`${BASE}/page/${pageId}/ai-generate`, body)
  return result || {}
}
